<mat-sidenav-container class="vct-sidenav-host">

  <!-- ── Main content ── -->
  <mat-sidenav-content>
    <div class="vct-page">

      <!-- Info banner -->
      <div class="org-banner" *ngIf="bannerVisible">
        <mat-icon class="org-banner-icon">info_outline</mat-icon>
        <div class="org-banner-text">
          <strong>VM Connection Templates</strong>
          <p>
            VM connection templates capture reusable Guacamole connection settings — protocol,
            clipboard sharing, file upload, screen recording, audio playback. Once attached
            to a class or to an org-level template VM, every launch in that scope picks up
            the template's <code>Allow*</code> flags. Detach to fall back to global defaults.
          </p>
        </div>
        <div class="org-banner-actions">
          <mat-checkbox [(ngModel)]="bannerDismissForever" class="banner-dismiss-check">
            Don't show again
          </mat-checkbox>
          <button mat-icon-button class="banner-close-btn"
                  (click)="dismissBanner()"
                  matTooltip="Dismiss">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>

      <!-- Toolbar -->
      <mat-card class="vct-toolbar-card">
        <mat-card-content class="vct-toolbar">

          <mat-form-field appearance="outline" class="search-field">
            <mat-label>Search templates</mat-label>
            <input matInput [(ngModel)]="searchTerm" (input)="onSearchChange()"
                   placeholder="Name or description…">
            <mat-icon matPrefix>search</mat-icon>
            <button *ngIf="searchTerm" matSuffix mat-icon-button
                    (click)="searchTerm=''; onSearchChange()">
              <mat-icon>close</mat-icon>
            </button>
          </mat-form-field>

          <!-- Protocol filter chips -->
          <div class="type-filters">
            <span class="filter-label">Filter:</span>
            <button class="type-filter-chip" [class.active]="rdpChipActive" (click)="toggleRdpChip()">
              <mat-icon class="chip-mat-icon">desktop_windows</mat-icon>
              RDP
            </button>
            <button class="type-filter-chip" [class.active]="vncChipActive" (click)="toggleVncChip()">
              <mat-icon class="chip-mat-icon">screen_share</mat-icon>
              VNC
            </button>
          </div>

          <span class="toolbar-spacer"></span>

          <button mat-raised-button color="primary" (click)="openCreatePanel()">
            <mat-icon>add</mat-icon>
            Add Connection Template
          </button>

        </mat-card-content>
      </mat-card>

      <!-- Table card -->
      <mat-card class="vct-table-card">
        <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

        <div *ngIf="error" class="vct-error">
          <mat-icon color="warn">error_outline</mat-icon>
          <span>{{ error }}</span>
          <button mat-button color="primary" (click)="loadTemplates()">
            <mat-icon>refresh</mat-icon> Retry
          </button>
        </div>

        <table mat-table [dataSource]="dataSource" matSort class="vct-table">

          <!-- Protocol -->
          <ng-container matColumnDef="protocol">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Protocol</th>
            <td mat-cell *matCellDef="let t">
              <span *ngIf="t.protocol === 'Rdp'" class="protocol-badge protocol-badge--rdp">
                <mat-icon class="badge-icon">desktop_windows</mat-icon>
                {{ protocolLabel(t.protocol) }}
              </span>
            </td>
          </ng-container>

          <!-- Template Name -->
          <ng-container matColumnDef="templateName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Template Name</th>
            <td mat-cell *matCellDef="let t" class="vct-name">{{ t.templateName }}</td>
          </ng-container>

          <!-- Description -->
          <ng-container matColumnDef="description">
            <th mat-header-cell *matHeaderCellDef>Description</th>
            <td mat-cell *matCellDef="let t" class="vct-desc-cell"
                [matTooltip]="(t.description?.length ?? 0) > 50 ? t.description : ''"
                matTooltipShowDelay="400">
              {{ t.description || '—' }}
            </td>
          </ng-container>

          <!-- Updated At -->
          <ng-container matColumnDef="updatedAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Last Updated</th>
            <td mat-cell *matCellDef="let t">{{ t.updatedAt | date:'dd MMM yyyy' }}</td>
          </ng-container>

          <!-- Actions -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let t" (click)="$event.stopPropagation()">
              <button *ngIf="canArchive" mat-icon-button matTooltip="Archive this template" (click)="archiveTemplate(t)"
                      aria-label="Archive template">
                <mat-icon>inventory_2</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"
              class="vct-row"
              (click)="onRowClick(row)"></tr>

          <tr class="mat-row" *matNoDataRow>
            <td class="mat-cell no-data-cell" [attr.colspan]="displayedColumns.length">
              <div class="no-data-message">
                <mat-icon>settings_input_hdmi</mat-icon>
                <p>{{ searchTerm || hasActiveFilter ? 'No templates match the current filters' : 'No VM connection templates yet — click "Add Connection Template" to create one.' }}</p>
              </div>
            </td>
          </tr>

        </table>

        <mat-paginator [pageSizeOptions]="pageSizeOptions" [pageSize]="pageSize" appPersistPageSize="vmConnectionTemplates:list" showFirstLastButtons></mat-paginator>
      </mat-card>

    </div>
  </mat-sidenav-content>

  <!-- ── Right-side details drawer ── -->
  <mat-sidenav #detailsDrawer position="end" mode="over"
               [opened]="showDetailDrawer"
               (closedStart)="closeDetailDrawer()"
               class="vct-details-sidenav">

    <div class="vct-drawer-header">
      <div class="vct-drawer-title">
        <mat-icon>settings_input_hdmi</mat-icon>
        <span>Template Details</span>
      </div>
      <button mat-icon-button (click)="closeDetailDrawer()" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-divider></mat-divider>

    <!-- Loading -->
    <div class="vct-drawer-body" *ngIf="loadingDetail">
      <div class="detail-loading">
        <mat-spinner diameter="32"></mat-spinner>
        <p>Loading details…</p>
      </div>
    </div>

    <!-- Loaded -->
    <div class="vct-drawer-body" *ngIf="selectedDetail && !loadingDetail">

      <!-- Rename form -->
      <div *ngIf="isRenaming" class="vct-rename-form">
        <mat-form-field appearance="outline" class="vct-rename-field">
          <mat-label>Template Name</mat-label>
          <input matInput [(ngModel)]="renameNameDraft" maxlength="100" required />
        </mat-form-field>
        <mat-form-field appearance="outline" class="vct-rename-field">
          <mat-label>Description</mat-label>
          <input matInput [(ngModel)]="renameDescDraft" maxlength="500" />
        </mat-form-field>
        <div *ngIf="renameError" class="vct-rename-error">
          <mat-icon color="warn">error_outline</mat-icon>
          <span>{{ renameError }}</span>
        </div>
        <div class="vct-rename-actions">
          <button mat-button (click)="cancelRename()">Cancel</button>
          <button mat-flat-button color="primary"
                  [disabled]="!renameNameDraft.trim()"
                  (click)="saveRename()">
            <mat-icon>check</mat-icon> Save
          </button>
        </div>
      </div>

      <!-- Read-only details (when not renaming) -->
      <app-view-vm-connection-template-details
        *ngIf="!isRenaming"
        [detail]="selectedDetail">
      </app-view-vm-connection-template-details>

      <!-- Action bar -->
      <div *ngIf="!isRenaming" class="vct-drawer-actions">
        <button mat-stroked-button color="primary" (click)="beginRename()">
          <mat-icon>edit</mat-icon> Rename
        </button>
        <button mat-stroked-button color="warn" (click)="deleteTemplate()">
          <mat-icon>delete</mat-icon> Delete
        </button>
      </div>

    </div>

  </mat-sidenav>

</mat-sidenav-container>

<!-- ── In-place vertical create panel (shared component) ── -->
<app-vm-connection-template-create-panel
  *ngIf="showCreatePanel"
  (cancelled)="closeCreatePanel()"
  (created)="onTemplateCreated($event)"
  (pickExisting)="onPickExistingDuplicate($event)">
</app-vm-connection-template-create-panel>
